import type { LeakageNullReason, LifecycleUnavailableReason } from '@/api/revenueLeakage'

/*
 * How the Leakage tab and the lifecycle panel word what the API says: each null's reason,
 * each code the website recorded, and the question each panel answers
 * (`api/src/competition_insights/modules/revenue/README.md` *Stage B*).
 */

/** Why a leakage figure is null: shown in its place, never a 0. */
export const LEAKAGE_NULL_TEXT: Record<LeakageNullReason, string> = {
  no_revenue: 'No revenue in the period',
  too_few_refunds: 'Fewer than 5 refunds with a completed order',
  no_coupon_source: 'Coupons are not recorded yet',
  needs_payment_events: 'Needs failed-payment events from the website',
  no_failures: 'No failed payments in the period',
  no_valued_failures: 'No failed payment carried a value',
}

export const LIFECYCLE_UNAVAILABLE_TEXT: Record<LifecycleUnavailableReason, string> = {
  not_found: 'not found in this scope',
  no_start_date: 'has no launch date',
  no_draw_date: 'has no draw or end date',
}

/** The question each panel's header asks. */
export const QUESTIONS = {
  refunds: 'Why did money go back?',
  discounts: 'What did we give away?',
  failed: 'What did the gateway cost us?',
  timeline: 'When did refunds land?',
  lifecycle: 'Is this competition keeping pace with the last ones?',
} as const

const REFUND_REASONS: Readonly<Record<string, string>> = {
  unknown: 'No reason recorded',
  fulfilment: 'Fulfilment',
  customer_request: 'Customer request',
  duplicate: 'Duplicate order',
  fraud: 'Fraud',
  cancelled: 'Cancelled',
}

/** A refund's `reason_category` (a code the website recorded) in words. */
export function refundReasonLabel(code: string): string {
  return REFUND_REASONS[code] ?? sentence(code.replaceAll('_', ' '))
}

const FAILURE_KINDS: Readonly<Record<string, string>> = {
  declined: 'Declined',
  '3ds': '3-D Secure',
  timeout: 'Timed out',
  cancelled: 'Cancelled',
  other: 'Other',
  unknown: 'Not given',
}

export function failureKindLabel(code: string): string {
  return FAILURE_KINDS[code] ?? sentence(code.replaceAll('_', ' '))
}

/** A payment provider's code (`cashflows`) as a name; `unknown` when none was sent. */
export function providerLabel(code: string): string {
  return code === 'unknown' ? 'Provider not given' : sentence(code.replaceAll('_', ' '))
}

export function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}
